import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, useLocation, useMatch } from 'react-router';

import { catchAll } from '@sorare/routing';

import { ErrorBoundary } from '@sorare/error-boundary';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import {
  ANY_SPORT_MARKET_HOME,
  MLB_MARKET_COMMON_MARKET,
  MLB_MARKET_COMMON_MARKET_ADD_CARD,
  MLB_MARKET_COMMON_MARKET_DROP_CARD,
  MLB_MARKET_INSTANT_BUY,
  MLB_MARKET_PRIMARY,
  MLB_MARKET_SECONDARY,
  MLB_MARKET_SECONDARY_PLAYERSLUG_RARITY,
} from '@sorare/core/src/constants/__generated__/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import {
  glossary,
  navLabels,
  transferMarket,
} from '@sorare/core/src/lib/glossary';
import {
  generateSportPath,
  matchAnySportPath,
} from '@sorare/core/src/lib/routing/generateSportPath';

import { MarketBanners } from '@sorare/marketplace/src/components/MarketBanners';
import { MarketHeader } from '@sorare/marketplace/src/components/MarketHeader';
import { DisabledMarket } from '@sorare/marketplace/src/components/market/DisabledMarket';
import { EnterFromAndroidMarketPlaceTab } from '@sorare/marketplace/src/components/mobileApp/EnterFromAndroidMarketplaceTab';
import { CurrentMarketplaceUserProvider } from '@sorare/marketplace/src/contexts/currentMarketplaceUser/Provider';
import {
  buildMarketTabRoute,
  useNavigableMarketTabParams,
} from '@sorare/marketplace/src/hooks/useNavigableMarketTabParams';

const MarketPage = () => {
  const {
    isIosApp,
    isMarketAuthorizedIosApp,
    isAndroidApp,
    canUseIosPostMessageAndForYou,
  } = useIsMobileApp();
  const { filterQuery, filterQueryWithInSeason } =
    useNavigableMarketTabParams();
  const {
    flags: {
      buyOnWebMarketplaceFromAndroid = false,
      useMarketHomePromoBanners = false,
    },
  } = useFeatureFlags();
  const { pathname } = useLocation();

  const matchCommonMarketRoute = useMatch(catchAll(MLB_MARKET_COMMON_MARKET));
  const isMarketHomeTab =
    useMarketHomePromoBanners &&
    !!matchAnySportPath(ANY_SPORT_MARKET_HOME, pathname);

  const showBanner = !matchCommonMarketRoute && !isMarketHomeTab;

  const disabledMarket =
    isAndroidApp && buyOnWebMarketplaceFromAndroid && !matchCommonMarketRoute;

  return (
    <FullWidthContainer>
      <NoIndex />
      <EnterFromAndroidMarketPlaceTab />
      {!isIosApp && <MarketHeader />}
      <ErrorBoundary fallback={null}>
        {!!showBanner && <MarketBanners />}
      </ErrorBoundary>
      <ContentWithStickyHeader
        hideHeader={!!isIosApp && !canUseIosPostMessageAndForYou}
        header={
          <FullWidthContainer.NoPadding>
            <TabBar fullWidth variant="flat" ignoreQueryParams>
              <Tab
                to={generateSportPath(ANY_SPORT_MARKET_HOME, {
                  sport: Sport.BASEBALL,
                })}
              >
                <FormattedMessage {...navLabels.tradeMarket} />
              </Tab>
              <Tab to={buildMarketTabRoute(MLB_MARKET_PRIMARY, filterQuery)}>
                <FormattedMessage {...navLabels.auctions} />
              </Tab>
              <Tab
                to={buildMarketTabRoute(MLB_MARKET_INSTANT_BUY, filterQuery)}
              >
                <FormattedMessage {...transferMarket.instantBuy} />
              </Tab>
              {!!(!isIosApp || isMarketAuthorizedIosApp) && (
                <Tab
                  to={buildMarketTabRoute(
                    MLB_MARKET_SECONDARY,
                    filterQueryWithInSeason
                  )}
                  matches={[
                    MLB_MARKET_SECONDARY,
                    MLB_MARKET_SECONDARY_PLAYERSLUG_RARITY,
                  ]}
                >
                  <FormattedMessage {...navLabels.purchases} />
                </Tab>
              )}
              <Tab
                to={MLB_MARKET_COMMON_MARKET_ADD_CARD}
                matches={[
                  MLB_MARKET_COMMON_MARKET_ADD_CARD,
                  MLB_MARKET_COMMON_MARKET_DROP_CARD,
                ]}
              >
                <FormattedMessage {...glossary.commonMarket} />
              </Tab>
            </TabBar>
          </FullWidthContainer.NoPadding>
        }
      >
        <Vertical gap={2}>
          <Suspense fallback={<LoadingIndicator fullHeight />}>
            <CurrentMarketplaceUserProvider sport={Sport.BASEBALL}>
              {disabledMarket ? <DisabledMarket /> : <Outlet />}
            </CurrentMarketplaceUserProvider>
          </Suspense>
        </Vertical>
      </ContentWithStickyHeader>
    </FullWidthContainer>
  );
};

export default MarketPage;
